/**
 * RolesAndPermissionsAddPopup
 *
 * Prototype dialog to create a new role with a permissions matrix. Uses local state
 * for the role name and selected permissions; currently does not persist to the API.
 * Save is disabled until a role name is provided and at least one permission is picked.
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  IconButton,
  TextField,
  Typography,
  Box,
  InputLabel,
  TableContainer,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./roles-and-permissions-add-popup.scss";

interface RolesAndPermissionsAddPopupProps {
  /** Whether the dialog is shown. */
  isOpen: boolean;
  /** Callback to close the dialog. */
  onClose: () => void;
}

const RolesAndPermissionsAddPopup: React.FC<
  RolesAndPermissionsAddPopupProps
> = ({ isOpen, onClose }) => {
  const [roleName, setRoleName] = useState("");
  const [isSelectAll, setIsSelectAll] = useState(false);

  const modules = [
    "Objectives",
    "Key Results",
    "Initiatives",
    "Milestones",
    "Statuses",
    "Permissions",
  ];
  const permissions = ["A", "R", "W", "D"];

  const [selectedPermissions, setSelectedPermissions] = useState<
    Record<string, Record<string, boolean>>
  >(
    modules.reduce(
      (acc, module) => ({
        ...acc,
        [module]: permissions.reduce(
          (pAcc, perm) => ({ ...pAcc, [perm]: false }),
          {},
        ),
      }),
      {},
    ),
  );

  const handlePermissionChange = (module: string, action: string) => {
    setSelectedPermissions((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: !prev[module][action],
      },
    }));
  };

  const handleSelectAll = () => {
    const newValue = !isSelectAll;
    setIsSelectAll(newValue);
    setSelectedPermissions(
      modules.reduce(
        (acc, module) => ({
          ...acc,
          [module]: permissions.reduce(
            (pAcc, perm) => ({ ...pAcc, [perm]: newValue }),
            {},
          ),
        }),
        {},
      ),
    );
  };
  const isSaveDisabled = () => {
    if (!roleName.trim()) return true; // role name empty
    return !Object.values(selectedPermissions).some((permObj) =>
      Object.values(permObj).includes(true),
    );
  };

  const handleSave = () => {
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      maxWidth="md"
      fullWidth
      className="add-role-popup"
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
    >
      {/* Header */}
      <DialogTitle className="popup-header">
        Add New Role
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className="popup-content">
        <InputLabel className="role-label">Role Name</InputLabel>
        <TextField
          fullWidth
          placeholder="Enter a role name"
          variant="outlined"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          margin="normal"
          className="role-input"
        />

        {/* Legend + Select All Row */}
        <Box className="legend-selectall-row">
          {/* Legend */}
          <Typography variant="body2" className="legend-text">
            <strong>A</strong> - Approvals | <strong>R</strong> - Read |{" "}
            <strong>W</strong> - Write | <strong>D</strong> - Delete
          </Typography>

          {/* Select All */}
          <Box className="select-all-box">
            <Checkbox checked={isSelectAll} onChange={handleSelectAll} />
            <span>Select All</span>
          </Box>
        </Box>

        {/* Permissions Table */}

        <TableContainer component={Paper} className="table-container">
          <Table className="permissions-table">
            <TableHead>
              <TableRow>
                <TableCell className="table-head">
                  Modules Permissions
                </TableCell>
                {permissions.map((perm) => (
                  <TableCell
                    key={perm}
                    align="center"
                    className="table-head-permissions"
                  >
                    {perm}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {modules.map((module) => (
                <TableRow key={module}>
                  <TableCell className="module-name">{module}</TableCell>
                  {permissions.map((action) => (
                    <TableCell key={action} align="center">
                      <Checkbox
                        checked={selectedPermissions[module][action]}
                        onChange={() => handlePermissionChange(module, action)}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* Footer */}
      <DialogActions className="popup-footer">
        <Button onClick={onClose} variant="outlined" className="secondary">
          Cancel
        </Button>
        <Box>
          <Button variant="outlined" sx={{ mr: 1 }} className="secondary">
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            className="primary"
            disabled={isSaveDisabled()}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RolesAndPermissionsAddPopup;
